const UNIT_RULES = {
  none: { value: 1, suffix: '' },
  m: { value: 1e6, suffix: ' M' },
  b: { value: 1e9, suffix: ' B' },
  t: { value: 1e12, suffix: ' T' },
};

const UNIT_VALUE_MAP = {
  ...UNIT_RULES,
  percent: { value: 1e-2, suffix: '%' },
};

const UnitArray = Object.values(UNIT_RULES);
export const addRoundParenWhenNeg =
  (_this, func) =>
  (...args) => {
    if (_this.value == null) {
      return '-';
    }
    if (_this.value !== Math.abs(_this.value)) {
      return `(${func.call(_this, ...args)})`;
    }
    return func.call(_this, ...args);
  };

export default class D_Number extends Number {
  constructor(value = null, unit = 'none') {
    super();
    this.unit = UNIT_VALUE_MAP[unit];
    this.value = null;
    if (typeof this.unit == 'undefined') {
      throw new Error('Invalid Unit of Number');
    }
    if (value === null) {
      return;
    }
    if (value.constructor === Number) {
      this.value = value / this.unit.value;
    } else if (value.constructor === String && !isNaN(parseFloat(value))) {
      this.value = parseFloat(value) / this.unit.value;
    }
  }

  static addSeparator(str, sep = ',') {
    if (!str) {
      return null;
    }
    const res = String(str);
    const parts = res.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    return parts.join('.');
  }

  static fixtoDollar(str) {
    if (!str && str !== 0) {
      return null;
    }
    if (String(str)[0] === '-') {
      return D_Number.addSeparator(`-$${String(str).replace(/^./, '')}`);
    }
    return D_Number.addSeparator(`$${String(str)}`);
  }

  static fixToUpper(value, decPoints, carry = 0) {
    const { value: unitValue, suffix } = UnitArray[carry];
    const res =
      decPoints < 0
        ? String(value / unitValue)
        : (value / unitValue).toFixed(decPoints);
    if (
      (carry === 0 && parseFloat(res) < 1e6) ||
      parseFloat(res) < 1000 ||
      carry > 2
    ) {
      return D_Number.addSeparator(res) + suffix;
    }
    return D_Number.fixToUpper(value, decPoints, carry + 1);
  }

  _toString(decPoints = 2) {
    if (decPoints > 3) {
      throw new Error('The decPoints must not greater than 3');
    }

    if (this.value == null) {
      return '-';
    }

    const absNum = Math.abs(this.value);
    if (this.unit.value === 1e-2) {
      return `${D_Number.addSeparator(absNum.toFixed(decPoints))}${
        this.unit.suffix
      }`;
    }

    return D_Number.fixToUpper(absNum, decPoints);
  }

  toString(decPoints = 2) {
    return addRoundParenWhenNeg(this, this._toString)(decPoints);
  }

  static format(value, unit, decPoints) {
    return new D_Number(value, unit).toString(decPoints);
  }

  static formatPercentage(value, decPoints) {
    return new D_Number(value, 'percent').toString(decPoints);
  }
  static formatWithoutSuffix(value, isNeg = false) {
    if (value !== 0 && !value) {
      return null;
    }
    if (value === '-' || value === 'TBD') {
      return value;
    }
    if (isNeg) {
      value = -value;
    }
    value = Math.round(value);
    const res = D_Number.addSeparator(value);
    if (value !== Math.abs(value)) {
      return `(${D_Number.addSeparator(Math.abs(value))})`;
    }
    return res;
  }
  static formatWithCertainSuffix(value, suffix, decPoints) {
    let formatNum, finalNum;
    if (typeof value !== 'number') {
      return '-';
    }
    if (suffix === '0') {
      formatNum = 1;
      suffix = '';
    } else {
      formatNum =
        (UnitArray.find((u) => u.suffix.trim() === suffix) || {}).value || '';
    }
    if (Math.abs(value / formatNum) < 10 && Math.abs(value / formatNum) > 0) {
      finalNum = `${Math.abs(value / formatNum).toFixed(decPoints)} ${suffix}`;
    } else {
      finalNum = `${Math.abs(value / formatNum).toFixed(0)} ${suffix}`;
    }

    return D_Number.addSeparator(finalNum);
  }
  getValue() {
    return this.value * this.unit.value;
  }

  isLessThan(comparison) {
    if (comparison.constructor === D_Number) {
      return this.getValue() < comparison.getValue();
    }
    return this.getValue() < comparison;
  }

  isGreaterThan(comparison) {
    if (comparison.constructor === D_Number) {
      return this.getValue() > comparison.getValue();
    }
    return this.getValue() > comparison;
  }

  isNotLessThan(comparison) {
    return !this.isGreaterThan(comparison);
  }

  isNotGreaterThan(comparison) {
    return !this.isLessThan(comparison);
  }
  static getArrayMaxNumber = (numbers_arr) =>
    Math.max(
      ...numbers_arr.map((y) => {
        if (y) {
          return y;
        }
        return 0;
      }),
    );
  static getArrayMinNumber = (numbers_arr) => {
    const min = Math.min(
      ...numbers_arr.map((y) => {
        if (y) {
          return y;
        }
        return 0;
      }),
    );
    if (min > 0) {
      return 0;
    }
    return min;
  };

  static getProperSuffix(value) {
    const lastCharInFormattedValue = D_Number.format(value).slice(-1);
    if (['M', 'B', 'T', '%'].includes(lastCharInFormattedValue)) {
      return lastCharInFormattedValue;
    }
    return '';
  }
}
